/**
 * QueuedFactModal
 * Displays encouraging facts that were queued based on user achievements/behaviors
 * Shows prominently with user-controlled dismissal
 */

import React, { useState, useEffect } from 'react';
import * as mixpanelService from '../services/mixpanelService';
import styles from '../styles/QueuedFactModal.module.css';

function QueuedFactModal({ onDismiss }) {
  const [fact, setFact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissing, setIsDismissing] = useState(false);

  // Fetch queued fact on mount
  useEffect(() => {
    async function fetchFact() {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const response = await fetch('/api/loading-screen/check-queued', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          console.error('[QueuedFactModal] Failed to fetch queued fact:', response.status);
          setIsLoading(false);
          return;
        }

        const data = await response.json();

        if (data.hasFact) {
          setFact({
            id: data.factId,
            text: data.message,
            type: data.factType
          });

          // Track Celebration Viewed
          mixpanelService.trackEvent('Celebration Viewed', {
            fact_id: data.factId,
            fact_type: data.factType,
            message_length: data.message?.length || 0,
          });
        }

        setIsLoading(false);

      } catch (error) {
        console.error('[QueuedFactModal] Error fetching queued fact:', error);
        setIsLoading(false);
      }
    }

    fetchFact();
  }, []);

  // Mark fact as displayed and dismiss
  const handleDismiss = async () => {
    if (!fact) {
      onDismiss();
      return;
    }

    // Track Celebration Dismissed
    mixpanelService.trackEvent('Celebration Dismissed', {
      fact_id: fact.id,
      fact_type: fact.type,
    });

    setIsDismissing(true);

    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        // Mark as displayed on backend
        await fetch(`/api/loading-screen/mark-displayed/${fact.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('[QueuedFactModal] Error marking fact as displayed:', error);
      // Continue with dismissal even if marking fails
    }

    // Animate out
    setTimeout(() => {
      onDismiss();
    }, 300);
  };

  // Don't render if loading or no fact
  if (isLoading || !fact) {
    return null;
  }

  // Determine icon based on fact type
  const getIcon = () => {
    switch (fact.type) {
      case 'celebration':
        return '🎉';
      case 'gentle_nudge':
        return '💪';
      case 'insight':
        return '💡';
      case 'milestone':
        return '🏆';
      default:
        return '✨';
    }
  };

  return (
    <div className={`${styles.overlay} ${isDismissing ? styles.fadeOut : ''}`}>
      <div className={`${styles.modal} ${isDismissing ? styles.slideOut : ''}`}>
        <div className={styles.iconContainer}>
          <span className={styles.icon}>{getIcon()}</span>
        </div>

        <div className={styles.content}>
          <h2 className={styles.title}>
            {fact.type === 'celebration' ? 'Celebrate!' :
             fact.type === 'gentle_nudge' ? 'Keep Going!' :
             fact.type === 'milestone' ? 'Milestone Reached!' :
             'Did You Know?'}
          </h2>

          <p className={styles.factText}>{fact.text}</p>
        </div>

        <button
          className={styles.continueButton}
          onClick={handleDismiss}
          disabled={isDismissing}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

export default QueuedFactModal;
